import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { consumePostAuthRedirect } from '@/lib/postAuthRedirect';
import AuthProviders from '@/components/feature/AuthProviders';
import { Logo } from '@/components/feature/Logo';
import { MeshBg } from '@/components/feature/MeshBg';

export default function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Default to /gallery (My Creatives) — returning users almost always
  // want to see their existing work first; new users get redirected to
  // /brands/new by AuthGate (no brands yet). Honors AuthGate's `from`
  // state when present. (Was /create May 26 → /gallery June 6.)
  const redirectPath = (location.state as { from?: string })?.from || '/gallery';

  useEffect(() => {
    if (user) {
      // A stashed trial-checkout redirect (from "Start Free Trial") wins over
      // the default landing / AuthGate `from`; otherwise use redirectPath.
      navigate(consumePostAuthRedirect() ?? redirectPath, { replace: true });
    }
  }, [user, navigate, redirectPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await signIn(email, password);
    setLoading(false);
    if (result.error) {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-surface flex items-center justify-center p-4">
      <MeshBg />
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center mb-4">
            <Logo height={73} />
          </a>
          <p className="text-dark-muted text-sm">Sign in to your account</p>
        </div>

        {/* Card */}
        <div className="gradient-stroke bg-white rounded-2xl p-8 shadow-2xl shadow-primary/45">
          {error && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium border border-red-200">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-dark mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-semibold text-dark">
                  Password
                </label>
                <a
                  href="/auth/forgot-password"
                  className="text-xs font-medium text-primary hover:text-primary-hover transition-colors"
                >
                  Forgot password?
                </a>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              ) : (
                <>
                  <i className="ri-login-box-line" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Google + Phone */}
          <AuthProviders />
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-dark-muted mt-6">
          Don&apos;t have an account?{' '}
          <a
            href="/auth/sign-up"
            className="font-semibold text-primary hover:text-primary-hover transition-colors"
          >
            Sign up
          </a>
        </p>
      </div>
    </div>
  );
}